import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';
import { industryQuotes } from '../data/industryQuotes';

// Industry icon component (matching LandingPage)
const IndustryIcon = ({ id, className = 'h-6 w-6' }) => {
  const common = { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', className };
  switch (id) {
    case 'painter':
      return (
        <svg {...common}>
          <rect x="5" y="5" width="10" height="4" rx="2"/>
          <path d="M15 7h3"/>
          <path d="M18 7v9"/>
        </svg>
      );
    case 'hvac':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="2"/>
          <path d="M12 4c2 0 3 1.5 3 3.5S13 11 12 11"/>
          <path d="M20 12c0 2-1.5 3-3.5 3S13 13 13 12"/>
          <path d="M12 20c-2 0-3-1.5-3-3.5S11 13 12 13"/>
          <path d="M4 12c0-2 1.5-3 3.5-3S11 11 11 12"/>
        </svg>
      );
    case 'pest-control':
      return (
        <svg {...common}>
          <path d="M9.5 8a2.5 2.5 0 0 1 5 0"/>
          <rect x="8" y="10" width="8" height="7" rx="3"/>
          <path d="M12 10v7"/>
          <path d="M8 12H5"/>
          <path d="M8 14.5H4.5"/>
          <path d="M8.5 16.5H6"/>
          <path d="M19 12h-3"/>
          <path d="M19.5 14.5H16"/>
          <path d="M18 16.5h-2.5"/>
          <path d="M10 6.5 8.5 5.5"/>
          <path d="M14 6.5 15.5 5.5"/>
        </svg>
      );
    case 'web-design':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="12" rx="2"/>
          <path d="M8 21h8"/>
          <path d="M12 17v4"/>
        </svg>
      );
    case 'wedding-photography':
      return (
        <svg {...common}>
          <rect x="4" y="7" width="16" height="12" rx="2"/>
          <circle cx="12" cy="13" r="3.5"/>
          <path d="M9 7l1.5-2h3L15 7"/>
        </svg>
      );
    case 'landscaping':
      return (
        <svg {...common}>
          <path d="M12 4c-4 2-7 6-7 9 0 4 3 6 7 7 4-1 7-3 7-7 0-3-3-7-7-9Z"/>
          <path d="M12 7v11"/>
        </svg>
      );
    case 'roofing':
      return (
        <svg {...common}>
          <path d="M3 11l9-7 9 7"/>
          <path d="M5 10v10h14V10"/>
        </svg>
      );
    case 'plumbing':
      return (
        <svg {...common}>
          <path d="M6 8h6"/>
          <path d="M9 6h2"/>
          <path d="M10 6v2"/>
          <path d="M12 8v3"/>
          <path d="M12 11h3"/>
          <path d="M14 16c0 1.1-.9 2-2 2s-2-.9-2-2c0-.9.7-2 2-3 1.3 1 2 2.1 2 3Z"/>
        </svg>
      );
    case 'electrical':
      return (
        <svg {...common}>
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8Z"/>
        </svg>
      );
    case 'solar':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/>
        </svg>
      );
    case 'pool-construction':
      return (
        <svg {...common}>
          <path d="M3 12c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/>
          <path d="M3 16c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/>
        </svg>
      );
    case 'interior-design':
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="6" rx="2"/>
          <path d="M6 11V9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"/>
          <path d="M4 17v2M20 17v2"/>
        </svg>
      );
    case 'catering':
      return (
        <svg {...common}>
          <path d="M6 4v5"/>
          <path d="M8 4v5"/>
          <path d="M10 4v5"/>
          <path d="M8 9v12"/>
          <circle cx="17" cy="7" r="2"/>
          <path d="M17 9v12"/>
        </svg>
      );
    case 'auto-detailing':
      return (
        <svg {...common}>
          <path d="M3 13h2l2-3h8l2 3h2"/>
          <circle cx="8" cy="16" r="1.6"/>
          <circle cx="16" cy="16" r="1.6"/>
          <path d="M5 16h14"/>
          <path d="M9 10h6"/>
        </svg>
      );
    case 'videography':
      return (
        <svg {...common}>
          <rect x="3" y="7" width="11" height="10" rx="2"/>
          <path d="M14 9l7-3v12l-7-3V9Z"/>
        </svg>
      );
    case 'tree-service':
      return (
        <svg {...common}>
          <path d="M12 5l-4 4h8l-4-4Z"/>
          <path d="M12 9l-5 5h10l-5-5Z"/>
          <path d="M12 13l-6 6h12l-6-6Z"/>
          <path d="M12 13v6"/>
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8"/>
        </svg>
      );
  }
};

const Examples = () => {
  useEffect(() => {
    analytics.track('Examples Page View');
  }, []);

  return (
    <SEOPageLayout
      title="Quote Examples | Interactive Pricing Demos | QuoteTier"
      description="Explore interactive quote examples across 15+ industries. See how Good/Better/Best pricing works for HVAC, painting, web design, photography, and more."
      canonical="/examples"
      ogTitle="Interactive Quote Examples - See How QuoteTier Works"
      ogDescription="Browse real quote examples for service businesses. See how interactive Good/Better/Best pricing helps customers choose higher-value options."
    >
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              See interactive quotes in <span className="text-primary font-bold">action</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Explore real quote examples across {industryQuotes.length}+ industries. Click any example to experience how customers interact with Good/Better/Best pricing.
            </p>
          </div>
        </div>
      </section>

      {/* Examples Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {industryQuotes.map((industry) => (
              <Link
                key={industry.id}
                to={`/demo-quote/${industry.id}`}
                onClick={() => analytics.track('Example Quote Clicked', { industry: industry.name })}
                className="group"
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg border border-gray-200 bg-white flex items-center justify-center">
                        <IndustryIcon id={industry.id} className="h-6 w-6 text-gray-900" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="font-instrument text-lg font-semibold text-gray-900 tracking-tight truncate">
                          {industry.name}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <CardDescription className="font-jetbrains text-sm text-gray-600 leading-relaxed">
                      {industry.description}
                    </CardDescription>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex justify-between text-xs font-jetbrains text-gray-500">
                        <span>Good: ${industry.tiers.good.price.toLocaleString()}</span>
                        <span>Better: ${industry.tiers.better.price.toLocaleString()}</span>
                        <span>Best: ${industry.tiers.best.price.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-4">
                    <Button
                      variant="outline"
                      className="w-full border-gray-300 hover:border-black hover:bg-black hover:text-white transition-all font-jetbrains"
                    >
                      Try Interactive Demo
                      <span className="ml-2 inline-block transition-transform group-hover:translate-x-0.5">→</span>
                    </Button>
                  </CardFooter>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold text-center mb-12">
            What makes these quotes different?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-instrument text-xl font-bold mb-3">Traditional PDF Quotes</h3>
              <ul className="space-y-3 font-jetbrains text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✕</span>
                  Static document - no interaction
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✕</span>
                  Single price - take it or leave it
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✕</span>
                  No tracking - did they even open it?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✕</span>
                  Print, sign, scan, email back
                </li>
              </ul>
            </div>
            <div className="bg-black text-white rounded-xl p-6">
              <h3 className="font-instrument text-xl font-bold mb-3">QuoteTier Interactive Quotes</h3>
              <ul className="space-y-3 font-jetbrains">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  Customers explore options themselves
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  Good/Better/Best drives upsells
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  Know exactly when they view it
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  Sign instantly on any device
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to create your own?
          </h2>
          <p className="font-jetbrains text-xl text-gray-600 mb-10">
            Join thousands of businesses using interactive quotes to win more deals.
          </p>
          <Link
            to="/signup"
            onClick={() => analytics.track('CTA Clicked', { location: 'examples-bottom', action: 'signup' })}
          >
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
              Start Free Today
            </Button>
          </Link>
          <p className="font-jetbrains text-sm text-gray-500 mt-4">
            No credit card required • 3 free quotes per month
          </p>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default Examples;
